import React, { useState, useCallback } from "react";
import { Calendar } from "lucide-react";
import { DatePickerModal } from "./date-picker-modal";
import { formatDate, parseDate } from "../../utils/dateTime";
import { UI_CONFIG } from "../../constants/autostack";
import { cn } from "../../lib/utils";

interface DateInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  label?: string;
  disablePastDates?: boolean;
  maxDate?: Date;
  minDate?: Date;
  error?: string;
}

export const DateInput: React.FC<DateInputProps> = React.memo(
  ({
    value,
    onChange,
    placeholder = "Select date",
    className = "",
    label = "Date",
    disablePastDates = false,
    maxDate,
    minDate,
    error,
  }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const parsedDate = parseDate(value);

    const handleDateSelect = useCallback(
      (date: Date) => {
        const formattedDate = formatDate(date);
        onChange?.(formattedDate);
        setIsModalOpen(false);
      },
      [onChange]
    );

    const handleOpenModal = useCallback(() => setIsModalOpen(true), []);
    const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

    return (
      <div className={className}>
        <div className="flex flex-col space-y-1">
          {label && (
            <label className="block text-sm text-[#6D6584] font-normal">
              {label}
            </label>
          )}
          <div className="relative">
            <button
              type="button"
              onClick={handleOpenModal}
              className={cn(
                "flex items-center justify-between w-full rounded-md bg-transparent border px-4 pr-12 text-left transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                "focus-visible:ring-[3px]",
                UI_CONFIG.FORM_HEIGHT,
                error
                  ? "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30"
                  : "border-input border-[#CECBD7] focus-visible:border-ring focus-visible:ring-ring/50"
              )}
              aria-label={`Select ${label}`}
            >
              <span className={value ? "text-gray-900" : "text-gray-500"}>
                {value || placeholder}
              </span>
            </button>
            <Calendar
              className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>
        {error && <p className="text-xs mt-1 text-red-500">{error}</p>}

        <DatePickerModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onDateSelect={handleDateSelect}
          selectedDate={parsedDate}
          title={`Select ${label}`}
          disablePastDates={disablePastDates}
          maxDate={maxDate}
          minDate={minDate}
        />
      </div>
    );
  }
);

DateInput.displayName = "DateInput";
